import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import { useMediaQuery, Box, useTheme, Paper, Skeleton, TextField, Typography } from "@mui/material";
import { Button, ChoiceToggle, CountrySelect, FieldWrapper, PageHeader, SectionHeader } from "@components/ui_v2";
import { CardResumenCotizacion, SliderCotizador } from "@components/ui_v2/cotizadoresComponents";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { TaxSystemConfigResponse } from "@interfaces/index";
import { TaxSystemCodes } from "@interfaces/store";
import { formatCurrency, buildTermOptions, getClosestTerm, formatCurrencyShort } from "@helpers/funciones";
import { PATHS_TOFU_V2 } from "@helpers/constantes";
import { PHONE_NUMBER } from "@utils/expreciones";
import useInterfaceConfig from "@hooks/useInterfaceConfig";

interface FormValues {
  amount: number;
  term: number;
  trade_name: string;
  country_code: string;
  phone: string;
}

/**
 * El régimen ya no se elige aquí: se cotiza con la config de PM y el registro elige el tax system según el RFC
 * (12 = PM, 13 = PFAE) con estos códigos, que viajan a `CrearCuenta` en `tax_system_codes`.
 */
const TAX_SYSTEM_CODES: TaxSystemCodes = { PM: "FLXPM", PFAE: "FLXPF" };

/** Opción "Otro plazo" del grupo de plazos y su tope, como en el prototipo ("Hasta 360 días."). */
const OTRO_PLAZO = "otro";
const MAX_DIAS_OTRO_PLAZO = 360;

const EMPTY_CONFIG: TaxSystemConfigResponse = {
  id: "",
  code: "",
  product_id: "",
  minAmount: 0,
  maxAmount: 0,
  stepAmount: 0,
  minTerm: 0,
  maxTerm: 0,
  stepTerm: 0,
  defaultAmount: 0,
  defaultTerm: 0,
  tax_system_type: "",
};

const validationSchema = yup.object().shape({
  trade_name: yup.string().trim().required("Campo requerido"),
  country_code: yup.string().required("Campo requerido"),
  phone: yup.string().required("Campo requerido").matches(PHONE_NUMBER, "Número de teléfono inválido"),
});

/**
 * Cotizador de factoraje de Flux (`Cotizador` del prototipo con `factoraje` y `contacto`): monto de la línea,
 * plazo de las facturas en días (los de la config, más "Otro plazo") y datos de contacto.
 */
const CotizadorFlux = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();

  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [cotizadorConfig, setCotizadorConfig] = useState<TaxSystemConfigResponse>(EMPTY_CONFIG);
  // "Otro plazo": el usuario escribe los días en lugar de elegir una de las opciones.
  const [otroPlazo, setOtroPlazo] = useState(false);
  const [plazoLibre, setPlazoLibre] = useState("");

  const formik = useFormik<FormValues>({
    initialValues: {
      amount: 0,
      term: 0,
      trade_name: "",
      country_code: "+52",
      phone: "",
    },
    validationSchema,
    onSubmit: (values) => {
      if (!cotizadorConfig.product_id || !values.amount || !values.term) {
        return;
      }
      const sourceCode = sessionStorage.getItem("source_code");

      let urlPortal = `${PATHS_TOFU_V2.crearCuenta.path}?tax_system_id=${cotizadorConfig.id}&product_id=${cotizadorConfig.product_id}&person_type=${cotizadorConfig.code}&loan_amount=${values.amount}&loan_term=${values.term}&tax_system_type=${cotizadorConfig.tax_system_type}`;
      urlPortal += `&tax_system_codes=${encodeURIComponent(JSON.stringify(TAX_SYSTEM_CODES))}`;
      // TODO: `CrearCuenta` (v2) todavía no lee los datos de contacto; se mandan para que los tome
      // en cuanto se conecte. `encodeURIComponent` porque la lada trae `+`.
      urlPortal += `&trade_name=${encodeURIComponent(values.trade_name.trim())}&code_phone_number=${encodeURIComponent(values.country_code)}&phone_number=${values.phone}`;
      if (sourceCode) {
        urlPortal += `&source_code=${sourceCode}`;
      }
      navigate(urlPortal);
    },
  });

  const { values, setFieldValue } = formik;

  const termOptions = useMemo(
    () => buildTermOptions(cotizadorConfig.minTerm, cotizadorConfig.maxTerm, cotizadorConfig.stepTerm),
    [cotizadorConfig.minTerm, cotizadorConfig.maxTerm, cotizadorConfig.stepTerm],
  );

  // Costo del anticipo: monto × tasa anual × días / 360 (fórmula del prototipo; la tasa es la de la marca).
  const costoAnticipo = useMemo(
    () =>
      values.amount && values.term
        ? values.amount * ((interfaceConfig?.interest_rate ?? 0) / 100) * (values.term / 360)
        : 0,
    [values.amount, values.term, interfaceConfig?.interest_rate],
  );

  const getInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      const responsePM = await getTaxSystemConfig(TAX_SYSTEM_CODES.PM as string).unwrap();
      setCotizadorConfig(responsePM);
    } catch (error) {
      // Mismo alcance que el legacy: sin config la pantalla queda en ceros, sin modal de error.
      Sentry.captureException(error, { extra: { component: "CotizadorFlux-initial_fetch" } });
    } finally {
      setIsLoading(false);
    }
  }, [getTaxSystemConfig]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  // Al llegar la config se toman sus defaults.
  useEffect(() => {
    const { minTerm, maxTerm, stepTerm, defaultAmount, defaultTerm } = cotizadorConfig;

    if (minTerm < maxTerm && stepTerm > 0) {
      setFieldValue("amount", defaultAmount);
      setFieldValue("term", getClosestTerm(buildTermOptions(minTerm, maxTerm, stepTerm), defaultTerm));
    }
  }, [cotizadorConfig, setFieldValue]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sourceCode = params.get("code");

    if (sourceCode) {
      sessionStorage.setItem("source_code", sourceCode);
    } else {
      sessionStorage.removeItem("source_code");
    }
  }, []);

  const handlePlazoLibre = (texto: string) => {
    const limpio = texto.replace(/[^0-9]/g, "").slice(0, 3);
    setPlazoLibre(limpio);
    const dias = Math.min(MAX_DIAS_OTRO_PLAZO, Math.max(1, Number(limpio) || 0));
    if (limpio) setFieldValue("term", dias);
  };

  const costoText = isNaN(costoAnticipo) || !isFinite(costoAnticipo) ? "$0.00" : formatCurrency(costoAnticipo);
  const bloqueado = isLoading || formik.isSubmitting;

  return (
    <Box>
      <PageHeader
        variant="cotizador"
        eyebrow="Cotización"
        title="Solicita tu línea de factoraje"
        subtitle="Línea revolvente para anticipar el cobro de tus facturas. Ajusta el monto de la línea y el plazo de las facturas."
      />

      <Paper variant="lendia_paper">
        <SectionHeader
          variant="cotizador"
          label="Monto de la línea"
          meta={`${formatCurrencyShort(cotizadorConfig.minAmount, true)} – ${formatCurrencyShort(cotizadorConfig.maxAmount, true)} MXN`}
          first
        />
        <SliderCotizador
          value={values.amount}
          onChange={(value) => setFieldValue("amount", value)}
          min={cotizadorConfig.minAmount}
          max={cotizadorConfig.maxAmount}
          step={cotizadorConfig.stepAmount}
          valueText={formatCurrencyShort(values.amount, true)}
          unit="MXN"
          minLabel={formatCurrencyShort(cotizadorConfig.minAmount, true)}
          maxLabel={formatCurrencyShort(cotizadorConfig.maxAmount, true)}
          loading={isLoading}
          testId="cotizador-flux-monto-slider"
        />

        <SectionHeader
          variant="cotizador"
          label="Plazo de las facturas"
          meta="Días que tus clientes tardan en pagarte"
        />
        {!isLoading ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* "Otro plazo" es una opción más del grupo; al elegirla se escriben los días. */}
            <ChoiceToggle
              variant="cotizador"
              value={otroPlazo ? OTRO_PLAZO : values.term}
              onChange={(value) => {
                if (value === OTRO_PLAZO) {
                  setOtroPlazo(true);
                  return;
                }
                setOtroPlazo(false);
                setPlazoLibre("");
                setFieldValue("term", value);
              }}
              options={[
                ...termOptions.map((term) => ({ value: term, label: `${term} días` })),
                { value: OTRO_PLAZO, label: "Otro plazo" },
              ]}
              testId="cotizador-flux-plazo"
            />
            {otroPlazo && (
              <FieldWrapper label="¿A cuántos días?" hint={`Hasta ${MAX_DIAS_OTRO_PLAZO} días.`}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <TextField
                    autoComplete="off"
                    size="lendia_input"
                    placeholder="ej. 45"
                    value={plazoLibre}
                    onChange={(e) => handlePlazoLibre(e.target.value)}
                    disabled={bloqueado}
                    sx={{ width: 125 }}
                    inputProps={{ inputMode: "numeric", "data-testid": "cotizador-flux-otro-plazo-input" }}
                  />
                  <Typography variant="body2" color="tokens.textSecondary">
                    días
                  </Typography>
                </Box>
              </FieldWrapper>
            )}
          </Box>
        ) : (
          <Skeleton variant="rounded" animation="wave" height={50} />
        )}

        <SectionHeader variant="cotizador" label="Tus datos de contacto" meta="Para enviarte tu oferta" />
        <Box
          sx={{ display: "grid", gridTemplateColumns: isMobileOrTablet ? "1fr" : "repeat(2, 1fr)", gap: "12px 14px" }}
        >
          <FieldWrapper
            label="Nombre comercial"
            required
            hint="Como te conocen tus clientes; puede ser distinto a tu razón social."
            error={(formik.touched.trade_name && formik.errors.trade_name) || ""}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="trade_name"
              placeholder="Textiles del Norte"
              value={values.trade_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={bloqueado}
              required
              inputProps={{ "aria-required": true, "data-testid": "cotizador-flux-nombre-comercial-input" }}
            />
          </FieldWrapper>
          <FieldWrapper
            label="Teléfono celular"
            required
            hint="A este número te contactamos y enviamos tu oferta por WhatsApp."
            error={(formik.touched.phone && formik.errors.phone) || ""}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <CountrySelect
                value={values.country_code}
                onChange={(value) => setFieldValue("country_code", value)}
                disabled={bloqueado}
              />
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="phone"
                placeholder="55 1234 5678"
                value={values.phone}
                onChange={(e) => setFieldValue("phone", e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
                onBlur={formik.handleBlur}
                disabled={bloqueado}
                required
                inputProps={{ "aria-required": true, "data-testid": "cotizador-flux-telefono-input" }}
              />
            </Box>
          </FieldWrapper>
        </Box>
      </Paper>

      <CardResumenCotizacion
        label="Costo estimado por anticipo"
        amount={costoText}
        caption={`MXN por anticipar ${formatCurrencyShort(values.amount, true)} a ${values.term || 0} días · línea revolvente`}
        disclaimer="Estimación inicial con fines informativos. La oferta final está sujeta a aprobación y al análisis de las facturas y sus deudores."
        actions={
          <Button
            tone="hero"
            withArrow
            onClick={formik.submitForm}
            disabled={bloqueado}
            data-testid="cotizador-flux-continue-button"
          >
            Solicitar financiamiento
          </Button>
        }
      />
    </Box>
  );
};
export default CotizadorFlux;
